import { ChangeEvent, FC, useRef } from 'react';

import UploadDocument, {
  UploadDocumentSizes,
} from 'modules/documents/components/UploadDocument';
import { useTranslate } from 'hooks/useTranslate';

import { useEncumbrancesExcelModal } from '../context/EncumbrancesExcelModalContext';

const TYPE_FILES_ALLOWED = '.xls,.xlsx';

const EncumbrancesExcelUploadView: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useEncumbrancesExcelModal();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChangeFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      actions.handleChangeExcelFile(selectedFile);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTriggerChangeFile = () => {
    fileInputRef.current?.click();
  };

  const uploadRules = [
    t('EncumbrancesExcelUploadRuleSheet'),
    t('EncumbrancesExcelUploadRuleEncumbranceId'),
    t('EncumbrancesExcelUploadRuleCif'),
    t('EncumbrancesExcelUploadRuleDate'),
  ];

  return (
    <div className="encumbrances-excel-modal__upload">
      <div className="card card-bordered encumbrances-excel-modal__upload-card">
        <div className="card-inner">
          <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap">
            <div className="d-flex">
              <em className="icon ni ni-file-xls text-primary mr-2 mt-1" />
              <div>
                <h6 className="mb-1">
                  {t('EncumbrancesExcelUploadCardTitle')}
                </h6>
                <p className="text-soft small mb-0">
                  {t('EncumbrancesExcelUploadCardDescription')}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-outline-primary btn-sm mt-2 mt-md-0"
              onClick={actions.handleDownloadExcelTemplate}
              data-testid="encumbrances-excel-download-template"
            >
              {t('EncumbrancesExcelDownloadTemplate')}
            </button>
          </div>

          {data.excelFile ? (
            <div className="encumbrances-excel-modal__file-chip d-flex justify-content-between align-items-center mb-3">
              <span>{data.excelFile.name}</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={handleTriggerChangeFile}
                disabled={data.isParsingExcel}
              >
                {t('EncumbrancesExcelChangeFile')}
              </button>
            </div>
          ) : (
            <UploadDocument
              inputLabel={t('EncumbrancesExcelUploadDragHint')}
              accept={TYPE_FILES_ALLOWED}
              size={UploadDocumentSizes.SMALL}
              file={data.excelFile}
              setFile={actions.handleChangeExcelFile}
            />
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept={TYPE_FILES_ALLOWED}
            className="d-none"
            onChange={handleChangeFileInput}
            data-testid="encumbrances-excel-file-input"
          />

          {data.isParsingExcel && (
            <div className="encumbrances-excel-modal__loading-strip d-flex align-items-center mt-3">
              <div
                className="spinner-border spinner-border-sm text-primary mr-2"
                role="status"
              />
              <span>{t('EncumbrancesExcelValidatingAndLoading')}</span>
            </div>
          )}

          <ul className="encumbrances-excel-modal__upload-rules text-soft small mt-3">
            {uploadRules.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>

          <p className="text-soft small mb-0 mt-2">
            {t('EncumbrancesExcelTemplateHint')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EncumbrancesExcelUploadView;
